import type { Ref } from 'vue'

import { mapGroup, mapPlayer, mapRoom } from './mappers'
import type { GroupNode, PlayerWithBuildsNode, RoomNode } from './mappers'
import type { GroupId, Player, PlayerGroup, Room } from '../../types'

export interface BoardSnapshot {
  readonly rooms: ReadonlyArray<RoomNode>
  readonly groups: ReadonlyArray<GroupNode>
  readonly players: ReadonlyArray<PlayerWithBuildsNode>
}

export interface BoardSnapshotRefs {
  readonly players: Ref<ReadonlyArray<Player>>
  readonly groups: Ref<ReadonlyArray<PlayerGroup>>
  readonly rooms: Ref<ReadonlyArray<Room>>
  readonly numericRoomId: Map<string, number>
  readonly numericGroupId: Map<string, number>
}

/**
 * Replace the live-board refs from one authoritative wire snapshot. Both the
 * initial `Board` query and a subscription event use this path, keeping the
 * stable-id maps and the visible room/group/player topology inseparable.
 */
export function applyBoardSnapshot(refs: BoardSnapshotRefs, snapshot: BoardSnapshot): void {
  refs.numericRoomId.clear()
  refs.numericGroupId.clear()
  for (const room of snapshot.rooms) {
    refs.numericRoomId.set(room.stableId || String(room.id), room.id)
  }
  for (const group of snapshot.groups) {
    refs.numericGroupId.set(group.stableId || String(group.id), group.id)
  }

  const groupStringIdByNum = new Map<number, GroupId>()
  for (const group of snapshot.groups) {
    groupStringIdByNum.set(group.id, group.stableId || String(group.id))
  }
  const groupIdsByRoomNum = new Map<number, GroupId[]>()
  for (const group of snapshot.groups) {
    const groupIds = groupIdsByRoomNum.get(group.roomId) ?? []
    groupIds.push(group.stableId || String(group.id))
    groupIdsByRoomNum.set(group.roomId, groupIds)
  }
  const playerIdsByGroupNum = new Map<number, string[]>()
  for (const player of snapshot.players) {
    const playerIds = playerIdsByGroupNum.get(player.player.groupId) ?? []
    playerIds.push(String(player.player.id))
    playerIdsByGroupNum.set(player.player.groupId, playerIds)
  }

  refs.rooms.value = snapshot.rooms.map((room) => ({
    ...mapRoom(room),
    groupIds: groupIdsByRoomNum.get(room.id) ?? [],
  }))
  refs.groups.value = snapshot.groups.map((group) => ({
    ...mapGroup(group),
    playerIds: playerIdsByGroupNum.get(group.id) ?? [],
  }))
  refs.players.value = snapshot.players.map((player) =>
    mapPlayer(
      player,
      groupStringIdByNum.get(player.player.groupId) ?? String(player.player.groupId),
    ),
  )
}
